import React from "react";
import { LocaleCurrency } from "helpers/Locale";
import "./cartTotalsNew.scss";
import "../cartLineItemsNew/cartLineItemsNew.scss";
import Tip from "components/tip/Tip";
import { ICart, ICartTaxLine, IDiscountLines } from "redux/reducers/models/cart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion, faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";
import { IOrder } from "redux/reducers/models/order";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRegisterContext } from "../RegisterContext";
import { useAppSelector } from "hooks/redux";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import ServiceFee from "components/serviceFee/ServiceFee";
import { displayPercent } from "helpers/Helpers";

interface IProps {
  subTotal: number;
  total: number;
  taxLines: Array<ICartTaxLine>;
  handleTipTeeSheet?: (tipAmount: number, tipAllocations?: Array<{ user_id: number; amount: number }>) => void;
  handleAddServiceFee?: (tipAmount: number) => void;
  handleClearServiceFee?: () => void;
  totalTip?: number;
  totalServiceFee?: number;
  currency?: string;
  order?: IOrder;
  cart?: ICart;
  handleDiscountClick?: (discountLine: IDiscountLines) => void;
  pointsToEarn?: number;
}

export default function CartTotalsNew(props: IProps) {
  const {
    currency,
    subTotal,
    total,
    taxLines,
    totalTip,
    totalServiceFee,
    handleTipTeeSheet,
    handleAddServiceFee,
    handleClearServiceFee,
    order,
    cart,
    handleDiscountClick,
    pointsToEarn,
  } = props;

  const { t } = useTranslation();
  const { lineItemsUpdating } = useRegisterContext();

  const { facilityStore } = useAppSelector(store => store);

  const isCartOpen = () => {
    return cart?.status === "open";
  };

  return (
    <div className="cart-totals-new carts-totals-new-margin-top">
      {cart?.discount_lines
        ?.filter(discount => discount?.application === "order")
        ?.map(discount => {
          const lineItemUpdating = lineItemsUpdating?.current?.find(item => item?.id === discount?.id);
          return (
            <div key={discount?.id} className="cart-line-items-new-discount-container">
              <div
                className={classNames("cart-line-items-new-discount-container-inner", {
                  "cart-line-items-new-discount-container-inner-clickable": handleDiscountClick && !lineItemUpdating,
                  [`cart-line-items-new-updating-${lineItemUpdating?.type}`]: !!lineItemUpdating,
                })}
                onClick={handleDiscountClick && !lineItemUpdating ? () => handleDiscountClick(discount) : undefined}
                style={{ justifyContent: "space-between" }}
              >
                <span className="cart-total-title">{discount?.title}</span>
                <p className="cart-line-items-new-discount">
                  {discount?.loyalty_points_used !== null
                    ? `[-${discount?.loyalty_points_used}${t("pts")}]:`
                    : `${discount?.title}:`}{" "}
                  -<LocaleCurrency currency={currency} amount={discount?.amount} />
                  {discount.notes ? (
                    <OverlayTrigger placement="auto" overlay={<Tooltip id="leftMenuToolTip">{discount.notes}</Tooltip>}>
                      <FontAwesomeIcon icon={faCircleQuestion} className="ml-2" />
                    </OverlayTrigger>
                  ) : null}
                </p>
              </div>
            </div>
          );
        })}

      {/* Loyalty points that will be earned */}
      {pointsToEarn ? (
        <div className="cart-total-row">
          <span className="cart-total-title">{t("Loyalty Points")}</span>
          <span className="cart-total-title cart-totals-new-points">
            +{pointsToEarn} {t("pts")}
          </span>
        </div>
      ) : null}

      <div className="cart-total-row">
        <span className="cart-total-title">{t("Subtotal")}</span>
        <span className="cart-total-title">
          {subTotal != null ? (
            <LocaleCurrency currency={currency ?? "cad"} amount={subTotal} />
          ) : (
            <FontAwesomeIcon className="cart-totals-new-spinner" size="1x" icon={faSpinnerThird} spin fade />
          )}
        </span>
      </div>

      <div>
        {taxLines &&
          taxLines?.map(tax_line => {
            return (
              <div key={tax_line.title} className="cart-total-row">
                <span className="cart-total-title">
                  {tax_line.title} <span className="cart-total-subtitle">{displayPercent(tax_line.rate, 3)}</span>
                </span>
                <span className="cart-total-title">
                  <LocaleCurrency currency={currency ?? "cad"} amount={tax_line.price} />
                </span>
              </div>
            );
          })}
      </div>

      <div className="cart-total-row">
        {totalTip > 0 || !!props?.order || cart?.status === "void" ? (
          <span className="cart-total-title">{t("Tip")}</span>
        ) : (
          <span></span>
        )}
        {totalTip != null ? (
          <span className="cart-total-title">
            <Tip
              currency={currency}
              handleTipTeeSheet={handleTipTeeSheet}
              totalTipAmount={totalTip}
              totalCartPrice={total}
              order={order}
            />
          </span>
        ) : (
          <FontAwesomeIcon className="cart-totals-new-spinner" size="1x" icon={faSpinnerThird} spin fade />
        )}
      </div>

      {facilityStore?.facility?.permissions?.enable_service_fees ? (
        <div className="cart-total-row">
          {(totalServiceFee > 0 && totalServiceFee !== null) || cart?.status === "void" ? (
            <span className="cart-total-title">
              Service Fee -{" "}
              <FontAwesomeIcon
                className={`cursor-pointer ${isCartOpen() ? "" : "pointer-events-none opacity-50"}`}
                size="1x"
                icon={faXmark}
                onClick={() => handleClearServiceFee()}
              />
            </span>
          ) : (
            <span></span>
          )}

          {totalServiceFee != null ? (
            <span className="cart-total-title">
              <ServiceFee
                currency={currency}
                handleAddServiceFee={handleAddServiceFee}
                totalServiceFeeAmount={totalServiceFee}
                totalCartPrice={total}
                order={order}
              />
            </span>
          ) : (
            <FontAwesomeIcon className="cart-totals-new-spinner" size="1x" icon={faSpinnerThird} spin fade />
          )}

          {/* <span className="cursor-pointer">
            <FontAwesomeIcon className="" size="1x" icon={faPlus} /> {" "}
            Add Service Fee
          </span> */}
        </div>
      ) : null}

      <div className="cart-total-row carts-totals-new-margin-top">
        <span className="cart-total-title cart-total-title-bold">{t("Total")}</span>
        <span className="cart-total-title cart-total-title-bold">
          {total != null ? (
            <LocaleCurrency currency={currency ?? "cad"} amount={total} />
          ) : (
            <FontAwesomeIcon className="cart-totals-new-spinner" size="1x" icon={faSpinnerThird} spin fade />
          )}
        </span>
      </div>
    </div>
  );
}
